import { Combobox } from "@foldkit/ui";
import { Option } from "effect";
import type { Html, HtmlBuilder } from "foldkit/html";

import type { TagUsage } from "../api/schemas/tags";
import { i18next } from "../i18n";
import { X } from "./icons";

/**
 * Tag input (KTD6): `@foldkit/ui` multi-select `Combobox` over the root's
 * tag suggestions, with the selection (the tag list) owned by the parent.
 * Behavior from v1 `TagInput.tsx` + `utils/tags.ts`: substring filtering,
 * an "Add" item for unmatched typed text, chips with a remove button, and
 * the no-whitespace rule with its inline error. Not ported: Backspace on an
 * empty input removing the last chip, comma as a delimiter, and the
 * dev-options switch between two implementations.
 */

export const Model = Combobox.Multi.Model;
export type Model = typeof Model.Type;
export const init = (id: string): Model => Combobox.Multi.init({ id });

export const Message = Combobox.Message;
export type Message = typeof Message.Type;
export const OutMessage = Combobox.OutMessage;
export type OutMessage = Combobox.OutMessage;

const bundle = Combobox.Multi.create<string>();
export const update = bundle.update;

export const TAG_VALIDATION_MESSAGE =
	"Tags cannot contain spaces. Use hyphens or underscores instead.";

/** v1 `sanitizeTag`: trimmed; empty or whitespace-containing labels are rejected. */
export const sanitizeTag = (raw: string): string | undefined => {
	const label = raw.trim();
	return label === "" || /\s/.test(label) ? undefined : label;
};

/** v1 `useTagSuggestions`: sanitized, deduped labels from `GET /tags`. */
export const suggestionsFrom = (
	tags: ReadonlyArray<TagUsage>,
): ReadonlyArray<string> =>
	Array.from(
		new Set(
			tags
				.map(({ tag }) => sanitizeTag(tag))
				.filter((tag): tag is string => tag !== undefined),
		),
	);

/**
 * v1 `TagInput.tsx:116-156`: suggestions containing the typed text
 * (case-insensitive), minus already-selected tags, plus the typed text
 * itself as a free-text "Add" item when nothing matches it exactly.
 */
export const items = (
	input: string,
	suggestions: ReadonlyArray<string>,
	selected: ReadonlyArray<string>,
): ReadonlyArray<string> => {
	const typed = input.trim();
	const needle = typed.toLowerCase();
	const matches = suggestions.filter(
		(tag) => !selected.includes(tag) && tag.toLowerCase().includes(needle),
	);
	return typed !== "" && !matches.some((tag) => tag === typed)
		? [...matches, typed]
		: matches;
};

export type ViewConfig<ParentMessage> = Readonly<{
	slotId: string;
	model: Model;
	selected: ReadonlyArray<string>;
	suggestions: ReadonlyArray<string>;
	ariaLabel: string;
	placeholder: string;
	error: Option.Option<string>;
	toParentMessage: (message: Message) => ParentMessage;
	onRemove: (tag: string) => ParentMessage;
}>;

/**
 * DaisyUI markup: chips as `badge`s on their own row above a full-width
 * input. The `@foldkit/ui` Combobox portals its listbox to `<body>` and
 * anchors it to the input wrapper, so the wrapper must be the full box
 * width for the panel to line up under the box.
 */
export const view = <ParentMessage>(
	h: HtmlBuilder<ParentMessage>,
	config: ViewConfig<ParentMessage>,
): Html => {
	const unique = Array.from(new Set(config.selected));
	const options = items(config.model.inputValue, config.suggestions, unique);
	return h.div(
		[],
		[
			h.div(
				[
					h.Class(
						`rounded-field border bg-base-100 p-1 ${config.model.isOpen ? "border-primary" : "border-base-300"}`,
					),
				],
				[
					h.div(
						[h.Class("flex flex-wrap gap-1")],
						unique.map((tag) =>
							h.keyed("button")(
								tag,
								[
									h.Type("button"),
									h.Class("badge badge-soft badge-primary gap-1"),
									h.AriaLabel(i18next.t("tagInput.remove", { tag })),
									h.OnClick(config.onRemove(tag)),
								],
								[h.span([], [tag]), X(h, { size: 12 })],
							),
						),
					),
					h.submodel({
						slotId: config.slotId,
						view: bundle.view,
						model: config.model,
						toParentMessage: config.toParentMessage,
						viewInputs: {
							items: options,
							selectedValues: unique,
							restingInputValue: "",
							itemToValue: (item: string) => item,
							itemToDisplayText: (item: string) => item,
							itemToConfig: (
								item: string,
								{ isActive }: { isActive: boolean },
							) => ({
								className: `cursor-pointer rounded-field px-3 py-1.5 ${isActive ? "bg-primary text-primary-content" : ""}`,
								content: h.span(
									[],
									[
										config.suggestions.includes(item)
											? item
											: i18next.t("tagInput.add", { tag: item }),
									],
								),
							}),
							ariaLabel: config.ariaLabel,
							inputPlaceholder: config.placeholder,
							className: "px-1 py-1",
							inputClassName: "w-full bg-transparent outline-none",
							// Above DaisyUI's .modal (z-index 999) for the add-record dialog.
							itemsClassName:
								"z-[1000] w-(--button-width) rounded-box border border-base-300 bg-base-100 p-1 shadow-lg",
							itemsScrollClassName: "max-h-[12.5rem] overflow-y-auto",
							anchor: { gap: 4 },
							openOnFocus: true,
						},
					}),
					config.model.isOpen && options.length === 0
						? h.p(
								[h.Class("px-3 py-1.5 text-sm text-base-content/60")],
								[i18next.t("tagInput.noMatch")],
							)
						: h.empty,
				],
			),
			Option.match(config.error, {
				onNone: () => h.empty,
				onSome: (error) =>
					h.p([h.Role("alert"), h.Class("mt-1 text-sm text-error")], [error]),
			}),
		],
	);
};
